import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'

const LOADED_FROM_LABEL = {
  DB_ACTIVE_VERSION: 'DB 버전',
  CLASSPATH_CSV: '샘플',
  LIVE_API: 'API 보강',
  EMPTY: '없음',
}

const TAB_LABELS = {
  'hira-institution': '요양기관',
  'kcd-master': 'KCD 상병마스터',
  'hira-disease-stats': 'HIRA 진료비통계지표',
  'kcis-coverage-codes': '신정원담보',
  'coverage-summary-rules': '보장요약룰',
}

const KCIS_DOMAIN_LABELS = {
  PERSON: '인보험',
  PROPERTY_LIABILITY: '화재·특종',
  AUTO: '자동차',
  PET: '반려동물',
}

const SOURCE_LINKS = {
  hiraInstitution: [
    { label: '공공데이터포털 병원정보서비스', url: 'https://www.data.go.kr/data/15001698/openapi.do' },
    { label: 'HIRA 보건의료빅데이터 Open API', url: 'https://opendata.hira.or.kr/op/opc/selectOpnsApi.do' },
  ],
  kcdMaster: [
    { label: 'HIRA 상병마스터 공지', url: 'https://www.hira.or.kr/rc/drug/insuadtcrtr/bbsView.do?brdBltNo=53037&brdScnBltNo=4&pgmid=HIRAA030069000400' },
    { label: '통계분류포털 KCD', url: 'https://kssc.kostat.go.kr' },
  ],
  hiraDiseaseStats: [
    { label: 'HIRA 진료비통계지표', url: 'https://www.hira.or.kr/bbsDummy.do?pgmid=HIRAA020045030000' },
    { label: 'HIRA 의료통계', url: 'https://opendata.hira.or.kr/op/opc/olapHthInsRvStatInfoTab21.do?docNo=03-037' },
  ],
  kcisCoverageCodes: [
    { label: '한국신용정보원 보험신용정보관리규약', url: 'https://www.kcredit.or.kr' },
  ],
}

function unwrapApiData(res) {
  const body = res?.data
  if (body?.success === false) {
    throw new Error(body?.message || '요청 처리에 실패했습니다.')
  }
  return body?.data !== undefined ? body.data : body
}

function formatDateTime(value) {
  return value ? formatApiDateTimeDisplay(undefined, value) : '-'
}

function formatNumber(value) {
  if (value == null || value === '') return '-'
  return Number(value || 0).toLocaleString('ko-KR')
}

function contentDispositionFilename(headerValue, fallback) {
  const value = String(headerValue || '')
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try { return decodeURIComponent(utf8Match[1]) } catch { /* ignore */ }
  }
  const plainMatch = value.match(/filename="?([^";]+)"?/i)
  return plainMatch?.[1] || fallback
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitForImportJob(jobKey, onUpdate) {
  if (!jobKey) return null
  const deadline = Date.now() + 30 * 60 * 1000
  let delay = 1500

  while (Date.now() < deadline) {
    const res = await api.get(`/operator/import-jobs/${encodeURIComponent(jobKey)}`, { __silent: true })
    const job = unwrapApiData(res) || {}
    onUpdate?.(job)

    if (job.status === 'SUCCEEDED') {
      return job
    }
    if (job.status === 'FAILED') {
      throw new Error(job.errorMessage || job.message || 'IMPORT_FAILED')
    }

    await sleep(delay)
    delay = Math.min(delay + 500, 5000)
  }

  throw new Error('IMPORT_JOB_TIMEOUT')
}

function StatusCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-slate-500">{sub}</div> : null}
    </div>
  )
}

function CompactMetric({ label, value, sub }) {
  return (
    <div className="flex min-h-[44px] items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className="text-sm font-bold text-slate-900">{value}</span>
      {sub ? <span className="text-xs text-slate-400">{sub}</span> : null}
    </div>
  )
}
function SourceLinks({ items }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-semibold text-slate-900">자료 출처</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {(items || []).map((item) => (
          <a
            key={item.url}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            href={item.url}
            target="_blank"
            rel="noreferrer"
          >
            {item.label} 바로가기
          </a>
        ))}
      </div>
    </div>
  )
}

function Message({ children, tone = 'slate' }) {
  if (!children) return null
  const toneClass = tone === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-50 text-slate-700'
  return <div className={`rounded-lg border px-3 py-2 text-sm ${toneClass}`}>{children}</div>
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`px-4 py-2 text-sm font-semibold ${
        active
          ? 'border-b-2 border-brand-navy text-brand-navy'
          : 'text-slate-500 hover:text-slate-700'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function HiraInstitutionTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [label, setLabel] = useState('')

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/admin/hira-medical-institutions/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || '상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('async', 'true')
    if (label.trim()) fd.append('label', label.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/admin/hira-medical-institutions/versions', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 병의원 정보를 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 병의원 정보를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('병의원 정보 업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: ${formatNumber(d?.entryCount)}건 반영`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || '업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const reload = async () => {
    setLoading(true)
    setMsg('')
    try {
      await api.post('/admin/hira-medical-institutions/reload')
      await loadStatus()
      setMsg('메모리에 다시 불러왔습니다.')
      setMsgTone('success')
    } catch (e) {
      setMsg(e?.message || '다시 불러오기에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="DB 건수" value={formatNumber(status?.dbActiveCount)} />
        <StatusCard label="기관명 인덱스" value={formatNumber(status?.nameIndexCount)} />
        <StatusCard label="보강 건수" value={formatNumber(status?.overrideCount)} />
        <StatusCard
          label="적용 소스"
          value={LOADED_FROM_LABEL[status?.loadedFrom] || '-'}
          sub={formatDateTime(status?.loadedAt)}
        />
      </div>

      <SourceLinks items={SOURCE_LINKS.hiraInstitution} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">HIRA 요양기관 업로드</h2>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">버전 라벨</label>
            <input
              type="text"
              className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="2026.3 병원정보서비스"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0])}
            />
            <button
              type="button"
              className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? '업로드 중' : 'CSV / XLSX 업로드'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={loading}
              onClick={reload}
            >
              다시 불러오기
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={loading}
              onClick={loadStatus}
            >
              상태 새로고침
            </button>
          </div>

          <Message tone={msgTone}>{msg}</Message>
        </div>
      </div>
    </div>
  )
}

function KcdMasterTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [sourceVersion, setSourceVersion] = useState('')
  const [sheetName, setSheetName] = useState('')
  const [replace, setReplace] = useState(true)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/kcd-master/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || 'KCD 마스터 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('replace', String(replace))
    fd.append('async', 'true')
    if (sourceVersion.trim()) fd.append('sourceVersion', sourceVersion.trim())
    if (sheetName.trim()) fd.append('sheetName', sheetName.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/kcd-master/import-xlsx', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 KCD 상병마스터를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: 처리 ${formatNumber(d?.processed)}건, 신규 ${formatNumber(d?.imported)}건, 갱신 ${formatNumber(d?.updated)}건`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || 'KCD 상병마스터 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const seedFromClasspath = async () => {
    setSeeding(true)
    setMsg('')
    try {
      const params = {
        replace,
        sourceVersion: sourceVersion.trim() || 'KCD_MASTER.csv',
      }
      const res = await api.post('/operator/kcd-master/seed-from-classpath', null, { params })
      const d = unwrapApiData(res)
      setMsg(`내장 CSV 반영 완료: 처리 ${formatNumber(d?.processed)}건, 신규 ${formatNumber(d?.imported)}건, 갱신 ${formatNumber(d?.updated)}건`)
      setMsgTone('success')
      await loadStatus()
    } catch (e) {
      setMsg(e?.message || '내장 CSV 반영에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setSeeding(false)
    }
  }

  const sourceVersions = Array.isArray(status?.sourceVersions) ? status.sourceVersions : []

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="활성 코드" value={formatNumber(status?.activeCount)} />
        <StatusCard label="전체 코드" value={formatNumber(status?.totalCount)} />
        <StatusCard label="별칭" value={formatNumber(status?.aliasCount)} />
        <StatusCard label="상태 확인" value={formatDateTime(status?.checkedAt)} />
      </div>

      <SourceLinks items={SOURCE_LINKS.kcdMaster} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">KCD 상병마스터 업로드</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">소스 버전</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={sourceVersion}
                onChange={(e) => setSourceVersion(e.target.value)}
                placeholder="예: 배포용 상병마스터_260329.xlsx"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">시트명</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="비워두면 자동 선택"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              기존 KCD 마스터를 교체
            </label>
          </div>

          <div className="space-y-3 rounded-lg bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-800">반영 작업</div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? '업로드 중' : 'XLSX 업로드'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={seeding}
                onClick={seedFromClasspath}
              >
                {seeding ? '반영 중' : '내장 CSV 반영'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={loading}
                onClick={loadStatus}
              >
                상태 새로고침
              </button>
            </div>
            <Message tone={msgTone}>{msg}</Message>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">최근 소스 버전</div>
        <div className="overflow-x-auto">
          <table className="min-w-[620px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">소스</th>
                <th className="px-3 py-2">버전</th>
                <th className="px-3 py-2 text-right">건수</th>
              </tr>
            </thead>
            <tbody>
              {sourceVersions.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={3}>등록된 소스 버전이 없습니다.</td>
                </tr>
              ) : null}
              {sourceVersions.map((row, idx) => (
                <tr key={`${row.sourceName}-${row.sourceVersion}-${idx}`} className="border-t">
                  <td className="px-3 py-2">{row.sourceName || '-'}</td>
                  <td className="px-3 py-2">{row.sourceVersion || '-'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.rowCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function statusBadge(status) {
  if (status === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  if (status === 'ARCHIVED') return 'bg-slate-100 text-slate-600 ring-slate-200'
  return 'bg-amber-50 text-amber-700 ring-amber-200'
}

function HiraDiseaseStatsTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [releases, setReleases] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [activatingId, setActivatingId] = useState(null)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')
  const [form, setForm] = useState({
    releaseName: '',
    periodStart: '',
    periodEnd: '',
    publishedAt: '',
    sourceUrl: '',
    notes: '',
    replace: true,
    activate: true,
  })

  const loadAll = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const [statusRes, releaseRes] = await Promise.all([
        api.get('/operator/hira-stats/status'),
        api.get('/operator/hira-stats/releases', {
          params: { datasetName: 'disease_code_rank', page: 1, size: 50 },
        }),
      ])
      setStatus(unwrapApiData(statusRes) || null)
      const releaseData = unwrapApiData(releaseRes) || {}
      setReleases(Array.isArray(releaseData.items) ? releaseData.items : [])
    } catch (e) {
      setStatus(null)
      setReleases([])
      setMsg(e?.message || 'HIRA 진료비통계지표 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('datasetName', 'disease_code_rank')
    fd.append('replace', String(form.replace))
    fd.append('activate', String(form.activate))
    fd.append('async', 'true')
    if (form.releaseName.trim()) fd.append('releaseName', form.releaseName.trim())
    if (form.periodStart) fd.append('periodStart', form.periodStart)
    if (form.periodEnd) fd.append('periodEnd', form.periodEnd)
    if (form.publishedAt) fd.append('publishedAt', form.publishedAt)
    if (form.sourceUrl.trim()) fd.append('sourceUrl', form.sourceUrl.trim())
    if (form.notes.trim()) fd.append('notes', form.notes.trim())
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/hira-stats/import-disease-xlsx', fd)
      let d = unwrapApiData(res)
      if (d?.jobKey) {
        setMsg(`업로드 접수됨: ${d.jobKey} · 서버에서 반영 중입니다.`)
        const job = await waitForImportJob(d.jobKey, (next) => {
          if (next.status === 'RUNNING') setMsg('서버에서 HIRA 진료비통계지표를 반영 중입니다.')
          if (next.status === 'QUEUED') setMsg('업로드가 접수되어 처리 대기 중입니다.')
        })
        d = job?.result || {}
      }
      setMsg(`업로드 완료: 반영 ${formatNumber(d?.imported)}건, KCD 연결 ${formatNumber(d?.matched)}건, 코드만 ${formatNumber(d?.unmatched)}건`)
      setMsgTone('success')
      await loadAll()
    } catch (e) {
      setMsg(e?.message || 'HIRA 진료비통계지표 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const activateRelease = async (releaseId) => {
    if (!releaseId) return
    setActivatingId(releaseId)
    setMsg('')
    try {
      await api.post(`/operator/hira-stats/releases/${releaseId}/activate`)
      setMsg('선택한 진료비통계지표 릴리스를 활성화했습니다.')
      setMsgTone('success')
      await loadAll()
    } catch (e) {
      setMsg(e?.message || '릴리스 활성화에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setActivatingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="릴리스" value={formatNumber(status?.releaseCount)} />
        <StatusCard label="활성 릴리스" value={formatNumber(status?.activeReleaseCount)} />
        <StatusCard label="통계 항목" value={formatNumber(status?.metricCount)} />
        <StatusCard label="코드만 매칭" value={formatNumber(status?.unmatchedMetricCount)} sub={formatDateTime(status?.checkedAt)} />
      </div>

      <SourceLinks items={SOURCE_LINKS.hiraDiseaseStats} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">HIRA 진료비통계지표 업로드</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">릴리스명</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.releaseName}
                onChange={(e) => updateForm('releaseName', e.target.value)}
                placeholder="예: 2023년 상반기 진료비통계지표"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">기간 시작</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.periodStart}
                onChange={(e) => updateForm('periodStart', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">기간 종료</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.periodEnd}
                onChange={(e) => updateForm('periodEnd', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">공표일</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.publishedAt}
                onChange={(e) => updateForm('publishedAt', e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">출처 URL</label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.sourceUrl}
                onChange={(e) => updateForm('sourceUrl', e.target.value)}
                placeholder="선택"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">메모</label>
              <textarea
                className="min-h-[80px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => updateForm('notes', e.target.value)}
                placeholder="자료 설명, 수집 경로 등"
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-800">반영 작업</div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={form.replace}
                onChange={(e) => updateForm('replace', e.target.checked)}
              />
              같은 릴리스명이 있으면 교체
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={form.activate}
                onChange={(e) => updateForm('activate', e.target.checked)}
              />
              업로드 후 바로 활성화
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? '업로드 중' : 'XLSX 업로드'}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={loading}
                onClick={loadAll}
              >
                상태 새로고침
              </button>
            </div>
            <Message tone={msgTone}>{msg}</Message>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">진료비통계지표 릴리스</div>
        <div className="overflow-x-auto">
          <table className="min-w-[860px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">릴리스</th>
                <th className="px-3 py-2">기간</th>
                <th className="px-3 py-2">공표일</th>
                <th className="px-3 py-2">상태</th>
                <th className="px-3 py-2">활성화</th>
                <th className="px-3 py-2 text-right">작업</th>
              </tr>
            </thead>
            <tbody>
              {releases.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500" colSpan={6}>등록된 진료비통계지표 릴리스가 없습니다.</td>
                </tr>
              ) : null}
              {releases.map((row) => (
                <tr key={row.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{row.releaseName || '-'}</div>
                    <div className="text-xs text-slate-500">{row.datasetName || '-'}</div>
                  </td>
                  <td className="px-3 py-2">{row.periodStart || '-'} ~ {row.periodEnd || '-'}</td>
                  <td className="px-3 py-2">{formatDateTime(row.publishedAt)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ring-1 ${statusBadge(row.status)}`}>
                      {row.status || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(row.activatedAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {row.status === 'ACTIVE' ? (
                      <span className="text-xs font-semibold text-emerald-700">사용 중</span>
                    ) : (
                      <button
                        type="button"
                        className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        disabled={activatingId === row.id}
                        onClick={() => activateRelease(row.id)}
                      >
                        {activatingId === row.id ? '활성화 중' : '활성화'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}



function CoverageNormalizationRulesTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [standards, setStandards] = useState([])
  const [rules, setRules] = useState([])
  const [query, setQuery] = useState('')
  const [ruleStatus, setRuleStatus] = useState('ALL')
  const [active, setActive] = useState('ALL')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/operator/coverage-normalization-rules/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || '보장정규화룰 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    }
  }, [])

  const loadLists = useCallback(async () => {
    setLoading(true)
    setMsg('')
    try {
      const [standardRes, ruleRes] = await Promise.all([
        api.get('/operator/coverage-normalization-rules/standards', { params: { q: query, status: ruleStatus, active, page: 1, size: 80 } }),
        api.get('/operator/coverage-normalization-rules/rules', { params: { q: query, status: ruleStatus, active, page: 1, size: 80 } }),
      ])
      const standardData = unwrapApiData(standardRes) || {}
      const ruleData = unwrapApiData(ruleRes) || {}
      setStandards(Array.isArray(standardData.items) ? standardData.items : [])
      setRules(Array.isArray(ruleData.items) ? ruleData.items : [])
    } catch (e) {
      setStandards([])
      setRules([])
      setMsg(e?.message || '보장정규화룰 목록을 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [active, query, ruleStatus])

  useEffect(() => { loadStatus() }, [loadStatus])
  useEffect(() => { loadLists() }, [active, ruleStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    await Promise.all([loadStatus(), loadLists()])
  }

  const search = async (e) => {
    e?.preventDefault?.()
    await loadLists()
  }

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/coverage-normalization-rules/upload', fd)
      const d = unwrapApiData(res) || {}
      setMsg(`업로드 완료: 표준보장 ${formatNumber(d.standardImported)}건 · 매칭룰 ${formatNumber(d.ruleImported)}건`)
      setMsgTone('success')
      await refresh()
    } catch (e) {
      setMsg(e?.message || '보장정규화룰 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const downloadXlsx = async () => {
    setDownloading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/coverage-normalization-rules/download', {
        params: { q: query, status: ruleStatus, active },
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadBlob(blob, contentDispositionFilename(res.headers?.['content-disposition'], `보장정규화룰_${new Date().toISOString().slice(0, 10)}.xlsx`))
      setMsg('엑셀 파일을 다운로드했습니다.')
      setMsgTone('success')
    } catch (e) {
      setMsg(e?.message || '보장정규화룰 다운로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setDownloading(false)
    }
  }


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <CompactMetric label="승인 표준보장" value={formatNumber(status?.standardApproved)} sub={`총 ${formatNumber(status?.standardTotal)}건`} />
        <CompactMetric label="승인 매칭룰" value={formatNumber(status?.ruleApproved)} sub={`총 ${formatNumber(status?.ruleTotal)}건`} />
        <CompactMetric label="초안 룰" value={formatNumber(status?.ruleDraft)} />
        <CompactMetric label="적용 정책" value="승인+사용" sub="승인된 사용 룰만 적용" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">보장정규화룰 관리</h2>
        </div>
        <form className="mt-4 grid gap-3 lg:grid-cols-[1fr_150px_130px_auto]" onSubmit={search}>
          <input type="search" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="담보명, 표준명, 코드, 키워드" />
          <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={ruleStatus} onChange={(e) => setRuleStatus(e.target.value)}>
            <option value="ALL">전체 상태</option>
            <option value="DRAFT">초안</option>
            <option value="APPROVED">승인</option>
            <option value="ARCHIVED">보관</option>
          </select>
          <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={active} onChange={(e) => setActive(e.target.value)}>
            <option value="ALL">전체 사용여부</option>
            <option value="ACTIVE">사용</option>
            <option value="INACTIVE">미사용</option>
          </select>
          <button type="submit" className="rounded-lg border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50" disabled={loading}>검색</button>
        </form>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
          <button type="button" className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? '업로드 중' : '엑셀 업로드'}</button>
          <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={downloading} onClick={downloadXlsx}>{downloading ? '다운로드 중' : '엑셀 다운로드'}</button>
          <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={loading} onClick={refresh}>새로고침</button>
        </div>
        <div className="mt-3"><Message tone={msgTone}>{msg}</Message></div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">표준보장</div>
          <div className="overflow-x-auto">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500"><tr><th className="px-3 py-2">표준코드</th><th className="px-3 py-2">표준명</th><th className="px-3 py-2">분류</th><th className="px-3 py-2">집계</th><th className="px-3 py-2">상태</th></tr></thead>
              <tbody>
                {standards.length === 0 ? <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={5}>{loading ? '불러오는 중입니다.' : '등록된 표준보장이 없습니다.'}</td></tr> : null}
                {standards.map((row) => <tr key={row.id || row.standard_code} className="border-t"><td className="px-3 py-2 font-mono text-xs">{row.standard_code}</td><td className="px-3 py-2 font-medium text-slate-900">{row.standard_name}</td><td className="px-3 py-2">{row.normalized_coverage_group}</td><td className="px-3 py-2">{row.default_aggregation_policy}</td><td className="px-3 py-2">{row.rule_status}{row.active ? '' : ' · 미사용'}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-sm font-semibold text-slate-900">매칭룰</div>
          <div className="overflow-x-auto">
            <table className="min-w-[860px] w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500"><tr><th className="px-3 py-2">룰명</th><th className="px-3 py-2">표준보장</th><th className="px-3 py-2">포함 키워드</th><th className="px-3 py-2">제외 키워드</th><th className="px-3 py-2">상태</th></tr></thead>
              <tbody>
                {rules.length === 0 ? <tr><td className="px-3 py-8 text-center text-slate-500" colSpan={5}>{loading ? '불러오는 중입니다.' : '등록된 매칭룰이 없습니다.'}</td></tr> : null}
                {rules.map((row) => <tr key={row.id || row.rule_name} className="border-t align-top"><td className="px-3 py-2 font-medium text-slate-900">{row.rule_name}</td><td className="px-3 py-2"><div>{row.standard_name || row.standard_code}</div><div className="font-mono text-xs text-slate-400">{row.standard_code}</div></td><td className="max-w-[240px] whitespace-pre-wrap px-3 py-2 text-xs">{row.include_keywords}</td><td className="max-w-[220px] whitespace-pre-wrap px-3 py-2 text-xs text-slate-500">{row.exclude_keywords || '-'}</td><td className="px-3 py-2">{row.rule_status}{row.active ? '' : ' · 미사용'}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

function CoverageOcrCorrectionsTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [items, setItems] = useState([])
  const [pageInfo, setPageInfo] = useState({ page: 1, size: 50, total: 0 })
  const [query, setQuery] = useState('')
  const [targetType, setTargetType] = useState('')
  const [reviewStatus, setReviewStatus] = useState('ALL')
  const [active, setActive] = useState('ALL')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/operator/coverage-ocr-corrections/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || 'OCR 보정 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    }
  }, [])

  const loadList = useCallback(async (nextPage = pageInfo.page) => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/coverage-ocr-corrections', {
        params: { q: query, targetType, reviewStatus, active, page: nextPage, size: pageInfo.size },
      })
      const data = unwrapApiData(res) || {}
      setItems(Array.isArray(data.items) ? data.items : [])
      setPageInfo({ page: data.page || nextPage, size: data.size || pageInfo.size, total: data.total || 0 })
    } catch (e) {
      setItems([])
      setMsg(e?.message || 'OCR 보정 목록을 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [active, pageInfo.page, pageInfo.size, query, reviewStatus, targetType])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  useEffect(() => {
    loadList(1)
  }, [active, reviewStatus, targetType]) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    await Promise.all([loadStatus(), loadList(pageInfo.page)])
  }

  const search = async (e) => {
    e?.preventDefault?.()
    await loadList(1)
  }

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/coverage-ocr-corrections/upload', fd)
      const d = unwrapApiData(res) || {}
      setMsg(`업로드 완료: 신규 ${formatNumber(d.imported)}건 · 갱신 ${formatNumber(d.updated)}건 · 제외 ${formatNumber(d.skipped)}건`)
      setMsgTone('success')
      await refresh()
    } catch (e) {
      setMsg(e?.message || 'OCR 보정 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const downloadXlsx = async () => {
    setDownloading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/coverage-ocr-corrections/download', {
        params: { q: query, targetType, reviewStatus, active },
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadBlob(blob, contentDispositionFilename(res.headers?.['content-disposition'], `OCR보정_${new Date().toISOString().slice(0, 10)}.xlsx`))
      setMsg('엑셀 파일을 다운로드했습니다.')
      setMsgTone('success')
    } catch (e) {
      setMsg(e?.message || 'OCR 보정 다운로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setDownloading(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil((pageInfo.total || 0) / (pageInfo.size || 50)))

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="승인 보정" value={formatNumber(status?.approvedCount)} />
        <StatusCard label="검수 대기" value={formatNumber(status?.pendingCount)} />
        <StatusCard label="상품명" value={formatNumber(status?.productCount)} />
        <StatusCard label="담보명" value={formatNumber(status?.coverageCount)} sub={formatDateTime(status?.checkedAt)} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">OCR 보정 관리</h2>
        </div>

        <form className="mt-4 grid gap-3 lg:grid-cols-[1fr_150px_150px_130px_auto]" onSubmit={search}>
          <input
            type="search"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="보험사, 상품명, 담보명, OCR 텍스트"
          />
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          >
            <option value="">전체 대상</option>
            <option value="PRODUCT_NAME">상품명</option>
            <option value="COVERAGE_NAME">담보명</option>
          </select>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={reviewStatus}
            onChange={(e) => setReviewStatus(e.target.value)}
          >
            <option value="ALL">전체 상태</option>
            <option value="PENDING">검수 대기</option>
            <option value="APPROVED">승인</option>
            <option value="REJECTED">반려</option>
          </select>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="ALL">전체 사용여부</option>
            <option value="true">사용</option>
            <option value="false">미사용</option>
          </select>
          <button
            type="submit"
            className="rounded-lg border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            disabled={loading}
          >
            검색
          </button>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <button
            type="button"
            className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? '업로드 중' : '엑셀 업로드'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={downloading}
            onClick={downloadXlsx}
          >
            {downloading ? '다운로드 중' : '엑셀 다운로드'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={loading}
            onClick={refresh}
          >
            새로고침
          </button>
        </div>
        <div className="mt-3">
          <Message tone={msgTone}>{msg}</Message>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">OCR 보정 목록</div>
          <div className="text-xs text-slate-500">총 {formatNumber(pageInfo.total)}건</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">대상</th>
                <th className="px-3 py-2">보험사</th>
                <th className="px-3 py-2">상품명</th>
                <th className="px-3 py-2">원문</th>
                <th className="px-3 py-2">보정값</th>
                <th className="px-3 py-2">표준담보</th>
                <th className="px-3 py-2">분류</th>
                <th className="px-3 py-2">상태</th>
                <th className="px-3 py-2 text-right">사용</th>
                <th className="px-3 py-2">수정일</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={10}>
                    {loading ? '불러오는 중입니다.' : '등록된 OCR 보정 데이터가 없습니다.'}
                  </td>
                </tr>
              ) : null}
              {items.map((row) => (
                <tr key={row.id} className="border-t align-top">
                  <td className="whitespace-nowrap px-3 py-2">{row.targetTypeLabel || row.targetType || '-'}</td>
                  <td className="whitespace-nowrap px-3 py-2">{row.insurerName || '-'}</td>
                  <td className="max-w-[220px] truncate px-3 py-2" title={row.productName || ''}>{row.productName || '-'}</td>
                  <td className="max-w-[260px] truncate px-3 py-2" title={row.rawText || ''}>{row.rawText || '-'}</td>
                  <td className="max-w-[260px] truncate px-3 py-2 font-medium text-slate-900" title={row.correctedText || ''}>{row.correctedText || '-'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    <div>{row.standardCoverageName || '-'}</div>
                    {row.standardCoverageCode ? <div className="font-mono text-slate-400">{row.standardCoverageCode}</div> : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{row.normalizedCoverageGroup || '-'}</td>
                  <td className="whitespace-nowrap px-3 py-2">{row.reviewStatusLabel || row.reviewStatus || '-'}</td>
                  <td className="px-3 py-2 text-right">{row.active ? '사용' : '미사용'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{formatDateTime(row.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40"
            disabled={loading || pageInfo.page <= 1}
            onClick={() => loadList(pageInfo.page - 1)}
          >
            이전
          </button>
          <div className="text-xs text-slate-500">{formatNumber(pageInfo.page)} / {formatNumber(totalPages)}</div>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40"
            disabled={loading || pageInfo.page >= totalPages}
            onClick={() => loadList(pageInfo.page + 1)}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  )
}

function KcisCoverageCodesTab() {
  const fileRef = useRef(null)
  const [status, setStatus] = useState(null)
  const [items, setItems] = useState([])
  const [pageInfo, setPageInfo] = useState({ page: 1, size: 50, total: 0 })
  const [query, setQuery] = useState('')
  const [domain, setDomain] = useState('')
  const [active, setActive] = useState('ALL')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('slate')

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/operator/kcis-coverage-codes/status')
      setStatus(unwrapApiData(res) || null)
    } catch (e) {
      setStatus(null)
      setMsg(e?.message || '신정원담보 상태를 불러오지 못했습니다.')
      setMsgTone('error')
    }
  }, [])

  const loadList = useCallback(async (nextPage = pageInfo.page) => {
    setLoading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/kcis-coverage-codes', {
        params: { q: query, domain, active, page: nextPage, size: pageInfo.size },
      })
      const data = unwrapApiData(res) || {}
      setItems(Array.isArray(data.items) ? data.items : [])
      setPageInfo({ page: data.page || nextPage, size: data.size || pageInfo.size, total: data.total || 0 })
    } catch (e) {
      setItems([])
      setMsg(e?.message || '신정원담보 목록을 불러오지 못했습니다.')
      setMsgTone('error')
    } finally {
      setLoading(false)
    }
  }, [active, domain, pageInfo.page, pageInfo.size, query])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  useEffect(() => {
    loadList(1)
  }, [active, domain]) // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault?.()
    await loadList(1)
  }

  const refresh = async () => {
    await Promise.all([loadStatus(), loadList(pageInfo.page)])
  }

  const uploadFile = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    setUploading(true)
    setMsg('')
    try {
      const res = await api.post('/operator/kcis-coverage-codes/upload', fd)
      const d = unwrapApiData(res) || {}
      setMsg(`업로드 완료: 신규 ${formatNumber(d.imported)}건, 갱신 ${formatNumber(d.updated)}건, 제외 ${formatNumber(d.skipped)}건`)
      setMsgTone('success')
      await refresh()
    } catch (e) {
      setMsg(e?.message || '신정원담보 업로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const downloadXlsx = async () => {
    setDownloading(true)
    setMsg('')
    try {
      const res = await api.get('/operator/kcis-coverage-codes/download', {
        params: { q: query, domain, active },
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadBlob(blob, contentDispositionFilename(res.headers?.['content-disposition'], `신정원담보목록_${new Date().toISOString().slice(0, 10)}.xlsx`))
      setMsg('엑셀 파일을 다운로드했습니다.')
      setMsgTone('success')
    } catch (e) {
      setMsg(e?.message || '신정원담보 다운로드에 실패했습니다.')
      setMsgTone('error')
    } finally {
      setDownloading(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil((pageInfo.total || 0) / (pageInfo.size || 50)))

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard label="활성 담보" value={formatNumber(status?.activeCount)} />
        <StatusCard label="전체 담보" value={formatNumber(status?.totalCount)} />
        <StatusCard label="인보험" value={formatNumber(status?.personCount)} />
        <StatusCard label="화재·특종" value={formatNumber(status?.propertyLiabilityCount)} sub={formatDateTime(status?.checkedAt)} />
      </div>

      <SourceLinks items={SOURCE_LINKS.kcisCoverageCodes} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
          <h2 className="text-base font-bold text-slate-900">신정원담보 관리</h2>
        </div>

        <form className="mt-4 grid gap-3 lg:grid-cols-[1fr_160px_140px_auto]" onSubmit={search}>
          <input
            type="search"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="담보코드, 담보명, 설명 검색"
          />
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          >
            <option value="">전체 영역</option>
            {Object.entries(KCIS_DOMAIN_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="ALL">전체 상태</option>
            <option value="true">사용</option>
            <option value="false">미사용</option>
          </select>
          <button
            type="submit"
            className="rounded-lg border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            disabled={loading}
          >
            검색
          </button>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
          <button
            type="button"
            className="rounded-lg border border-teal-600 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? '업로드 중' : '엑셀 업로드'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={downloading}
            onClick={downloadXlsx}
          >
            {downloading ? '다운로드 중' : '엑셀 다운로드'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={loading}
            onClick={refresh}
          >
            새로고침
          </button>
        </div>
        <div className="mt-3">
          <Message tone={msgTone}>{msg}</Message>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">신정원담보 목록</div>
          <div className="text-xs text-slate-500">총 {formatNumber(pageInfo.total)}건</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
              <tr>
                <th className="px-3 py-2">보험영역</th>
                <th className="px-3 py-2">담보코드</th>
                <th className="px-3 py-2">신정원담보명</th>
                <th className="px-3 py-2">설명</th>
                <th className="px-3 py-2">문서</th>
                <th className="px-3 py-2">상태</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>
                    {loading ? '불러오는 중입니다.' : '등록된 신정원담보가 없습니다.'}
                  </td>
                </tr>
              ) : null}
              {items.map((row) => (
                <tr key={row.id} className="border-t align-top">
                  <td className="whitespace-nowrap px-3 py-2">{row.insuranceDomainLabel || KCIS_DOMAIN_LABELS[row.insuranceDomain] || '-'}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700">{row.coverageCode || '-'}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{row.coverageName || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.description || '-'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    <div>{row.sourceDocName || '-'}</div>
                    <div>{row.sourceDocRevision || '-'}{row.sourcePage ? ` · ${row.sourcePage}p` : ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ring-1 ${row.active ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
                      {row.active ? '사용' : '미사용'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40"
            disabled={loading || pageInfo.page <= 1}
            onClick={() => loadList(pageInfo.page - 1)}
          >
            이전
          </button>
          <div className="text-xs text-slate-500">{formatNumber(pageInfo.page)} / {formatNumber(totalPages)}</div>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:opacity-40"
            disabled={loading || pageInfo.page >= totalPages}
            onClick={() => loadList(pageInfo.page + 1)}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  )
}

function CoverageNormalizationRuleSingleTestPanel() {
  const [testForm, setTestForm] = useState({ insuranceCategory: '', productName: '', coverageNameRaw: '', standardCoverageName: '' })
  const [testResult, setTestResult] = useState(null)

  const setTestValue = (key, value) => setTestForm((prev) => ({ ...prev, [key]: value }))

  const runTest = async () => {
    setTestResult(null)
    try {
      const res = await api.post('/operator/coverage-normalization-rules/test', testForm)
      setTestResult(unwrapApiData(res) || {})
    } catch (e) {
      setTestResult({ error: e?.message || '룰 테스트에 실패했습니다.' })
    }
  }

  const renderOutcome = (title, code, name, group, meta) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="text-xs font-semibold text-slate-500">{title}</div>
      <div className="mt-2 text-sm font-bold text-slate-900">{name || '-'}</div>
      <div className="mt-1 text-xs text-slate-600">분류: {group || '-'}</div>
      <div className="mt-1 font-mono text-[11px] text-slate-400">{code || '-'}</div>
      {meta ? <div className="mt-2 text-xs text-slate-500">{meta}</div> : null}
    </div>
  )

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-sm font-semibold text-slate-900">단건 룰 테스트</div>
      <div className="mt-3 grid gap-3 lg:grid-cols-5">
        <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={testForm.insuranceCategory} onChange={(e) => setTestValue('insuranceCategory', e.target.value)} placeholder="보험구분" />
        <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={testForm.productName} onChange={(e) => setTestValue('productName', e.target.value)} placeholder="상품명" />
        <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={testForm.coverageNameRaw} onChange={(e) => setTestValue('coverageNameRaw', e.target.value)} placeholder="원문 담보명" />
        <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={testForm.standardCoverageName} onChange={(e) => setTestValue('standardCoverageName', e.target.value)} placeholder="신정원/표준담보명" />
        <button type="button" className="rounded-lg border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-semibold text-white" onClick={runTest}>테스트</button>
      </div>
      {testResult ? (
        testResult.error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{testResult.error}</div>
        ) : (
          <div className="mt-3 space-y-3">
            <div className={`rounded-lg border px-3 py-2 text-sm ${testResult.changedFromHardcoded ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
              {testResult.changedFromHardcoded ? '기존 하드코딩 기준과 다른 결과입니다.' : '기존 하드코딩 기준과 동일한 결과입니다.'}
            </div>
            <div className="grid gap-3 lg:grid-cols-3">
              {renderOutcome('최종 적용', testResult.finalStandardCoverageCode, testResult.finalStandardCoverageName, testResult.finalNormalizedCoverageGroup, testResult.matchedBy || '-')}
              {renderOutcome('하드코딩 기준', testResult.hardcodedStandardCoverageCode, testResult.hardcodedStandardCoverageName, testResult.hardcodedNormalizedCoverageGroup, '기존 코드 룰 기준')}
              {renderOutcome('DB 승인룰', testResult.standardCoverageCode, testResult.standardCoverageName, testResult.normalizedCoverageGroup, testResult.matched ? (testResult.ruleName || 'DB_RULE') : '매칭 없음')}
            </div>
          </div>
        )
      ) : null}
    </div>
  )
}
function CoverageSummaryRuleTestTab() {
  return (
    <div className="space-y-4">
      <CoverageSummaryRuleWholeTestPanel />
      <CoverageNormalizationRuleSingleTestPanel />
    </div>
  )
}
function CoverageSummaryRuleWholeTestPanel() {
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)
  const [running, setRunning] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')
  const diagnostics = result?.diagnostics || {}
  const overview = diagnostics?.overview || {}
  const reasons = Array.isArray(diagnostics?.reasons) ? diagnostics.reasons : []
  const normalizationDiffs = Array.isArray(diagnostics?.normalizationDiffs) ? diagnostics.normalizationDiffs : []
  const rows = Array.isArray(result?.summaryReport?.rows) ? result.summaryReport.rows : []

  const isExcelFile = (selected) => {
    const name = String(selected?.name || '').toLowerCase()
    return name.endsWith('.xlsx') || name.endsWith('.xls') || String(selected?.type || '').includes('spreadsheet')
  }

  const selectFile = (selected) => {
    setFile(selected || null)
    setResult(null)
    setError('')
  }

  const runTest = async () => {
    if (!file) {
      setError('테스트할 파일을 선택해 주세요.')
      return
    }
    setRunning(true)
    setError('')
    setResult(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const endpoint = isExcelFile(file)
        ? '/ops/operation-tests/coverage/summary/workbook'
        : '/ops/operation-tests/coverage/summary'
      const res = await api.post(endpoint, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        __silent: true,
      })
      setResult(unwrapApiData(res) || null)
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || '보장요약 전체 테스트에 실패했습니다.')
    } finally {
      setRunning(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const downloadSummaryExcel = async () => {
    if (!result) {
      setError('먼저 전체 테스트를 실행해 주세요.')
      return
    }
    setDownloading(true)
    setError('')
    try {
      const res = await api.post('/ops/operation-tests/coverage/summary/excel', {
        draft: result.draft || null,
        summaryReport: result.summaryReport || null,
      }, {
        responseType: 'blob',
        __silent: true,
      })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadBlob(blob, contentDispositionFilename(res.headers?.['content-disposition'], `운영테스트_보장요약리포트_${new Date().toISOString().slice(0, 10)}.xlsx`))
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || '보장요약 테스트 엑셀을 다운로드하지 못했습니다.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">보장요약 전체 테스트</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.pdf,.txt,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => selectFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => fileRef.current?.click()}
            disabled={running}
          >
            파일 선택
          </button>
          <button
            type="button"
            className="rounded-lg border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            onClick={runTest}
            disabled={running || !file}
          >
            {running ? '테스트 중' : '전체 테스트'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={downloadSummaryExcel}
            disabled={downloading || !result}
          >
            {downloading ? '다운로드 중' : '결과 엑셀 다운로드'}
          </button>
        </div>
      </div>

      <div className="mt-3 text-sm text-slate-700">
        선택 파일: {file?.name || '보유계약목록/보장요약리포트 엑셀 또는 보장분석 PDF'}
      </div>
      {error ? <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

      {result ? (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <StatusCard label="계약" value={formatNumber(overview.contractCount)} />
            <StatusCard label="세부담보" value={formatNumber(overview.coverageCount)} />
            <StatusCard label="요약 행" value={formatNumber(overview.summaryRowCount)} />
            <StatusCard label="검수 후보" value={formatNumber(overview.reviewCandidateCount)} />
            <StatusCard label="금액 미추출" value={formatNumber(overview.zeroAmountCoverageCount)} />
            <StatusCard label="하드코딩 차이" value={formatNumber(overview.normalizationDiffCount)} />
          </div>

          {reasons.length > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">진단 메모</div>
              <div className="mt-2 space-y-2">
                {reasons.slice(0, 6).map((reason, idx) => (
                  <div key={`${reason.title || idx}-${idx}`} className="rounded border border-slate-200 bg-white px-3 py-2 text-sm">
                    <div className="font-semibold text-slate-900">{reason.level || '-'} · {reason.title || '-'}</div>
                    <div className="mt-0.5 text-xs text-slate-600">{reason.detail || '-'}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {normalizationDiffs.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-sm font-semibold text-amber-900">하드코딩 룰 대비 차이</div>
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-[960px] w-full text-xs">
                  <thead className="text-left text-amber-800"><tr><th className="px-2 py-1">보험사</th><th className="px-2 py-1">상품</th><th className="px-2 py-1">담보</th><th className="px-2 py-1">현재</th><th className="px-2 py-1">하드코딩</th></tr></thead>
                  <tbody>
                    {normalizationDiffs.slice(0, 8).map((row, idx) => (
                      <tr key={`${row.productName || idx}-${idx}`} className="border-t border-amber-200 align-top">
                        <td className="px-2 py-1">{row.insurerName || '-'}</td>
                        <td className="px-2 py-1">{row.productName || '-'}</td>
                        <td className="px-2 py-1">{row.coverageNameRaw || '-'}</td>
                        <td className="px-2 py-1">{row.currentNormalizedCoverageGroup || '-'} · {row.currentStandardCoverageName || '-'}</td>
                        <td className="px-2 py-1">{row.hardcodedNormalizedCoverageGroup || '-'} · {row.hardcodedStandardCoverageName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">보장요약 결과 미리보기</div>
            <div className="overflow-x-auto">
              <table className="min-w-[720px] w-full text-sm">
                <thead className="bg-white text-left text-xs font-semibold text-slate-500">
                  <tr>
                    <th className="px-3 py-2">분류</th>
                    <th className="px-3 py-2">보장</th>
                    <th className="px-3 py-2 text-right">합계</th>
                    <th className="px-3 py-2 text-right">상품 수</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 12).map((row, idx) => (
                    <tr key={`${row.coverageCode || row.coverageName || idx}-${idx}`} className="border-t">
                      <td className="px-3 py-2">{row.coverageGroup || '-'}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.coverageName || '-'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.totalAmount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(Array.isArray(row.products) ? row.products.length : 0)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 ? (
                    <tr><td className="px-3 py-6 text-center text-slate-500" colSpan={4}>생성된 보장요약 행이 없습니다.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
function CoverageSummaryRulesTab() {
  const [section, setSection] = useState('normalization')

  const buttonClass = (key) => `rounded-lg border px-4 py-2 text-sm font-semibold ${
    section === key
      ? 'border-brand-navy bg-brand-navy text-white'
      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
  }`

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={buttonClass('normalization')} onClick={() => setSection('normalization')}>
          정규화 룰
        </button>
        <button type="button" className={buttonClass('ocr')} onClick={() => setSection('ocr')}>
          OCR 보정
        </button>
        <button type="button" className={buttonClass('test')} onClick={() => setSection('test')}>
          테스트
        </button>
      </div>

      {section === 'normalization' ? <CoverageNormalizationRulesTab /> : null}
      {section === 'ocr' ? <CoverageOcrCorrectionsTab /> : null}
      {section === 'test' ? <CoverageSummaryRuleTestTab /> : null}
    </div>
  )
}
export default function RawDataAdminPage() {
  const [tab, setTab] = useState('hira-institution')

  return (
    <div className="mx-auto max-w-[1760px] px-4 py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
            <h1 className="text-xl font-bold text-slate-900">RAW 데이터관리</h1>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {Object.entries(TAB_LABELS).map(([key, label]) => (
          <TabButton key={key} active={tab === key} onClick={() => setTab(key)}>
            {label}
          </TabButton>
        ))}
      </div>

      {tab === 'hira-institution' ? <HiraInstitutionTab /> : null}
      {tab === 'kcd-master' ? <KcdMasterTab /> : null}
      {tab === 'hira-disease-stats' ? <HiraDiseaseStatsTab /> : null}
      {tab === 'kcis-coverage-codes' ? <KcisCoverageCodesTab /> : null}
      {tab === 'coverage-summary-rules' ? <CoverageSummaryRulesTab /> : null}

      <div className="mt-8 text-xs text-slate-500">
        <Link className="text-brand-navy hover:underline" to="/data/design-support-reference">
          질병·특약·수술명 관리
        </Link>
        {' · '}
        <Link className="text-brand-navy hover:underline" to="/data/insurers">
          원수사 마스터
        </Link>
      </div>
    </div>
  )
}
